/**
 * Сборка плоского payload для сохранения расчёта бэкендом.
 *
 * Переехал сюда из `widgets/header/Header.tsx`: изначально payload собирал
 * только Header (сохранение в standalone-историю), теперь то же самое делает и
 * `widgets/product-history` (сохранение расчёта к карточке товара платформы).
 * Второй независимый формат payload означал бы, что сохранённое из embed не
 * читается в standalone — сохранённые записи в БД общие для обоих режимов.
 *
 * Принимает структурное подмножество `CalculatorData`/`SalesPlanData`
 * (`widgets/calculator`, `widgets/forecast`), а не сами эти типы: `shared` не
 * имеет права импортировать из `widgets` (STRUCTURE.md — импорт только вниз по
 * слоям). Структурная типизация TS даёт совместимость без импорта — вызывающий
 * код в `widgets/*` передаёт свои настоящие объекты, они удовлетворяют форме
 * ниже автоматически.
 */

export interface CalculatorPayloadSource {
  asin?: string;
  purchasePrice: number;
  shippingPrice: number;
  shippingCostPerKg: number;
  unitWeightKg: number;
  storageFee: number;
  sellingPrice: number;
  referralFee: number;
  fbaFee: number;
  otherFees: number;
  ppcCost: number;
  conversionRate: number;
  ppcSalesPercentage: number;
  returnRate: number;
  resellableReturns: number;
  monthlySales: number;
  batchQuantity: number;
  couponPromoEnabled?: boolean;
  competitorAsins?: string[];
  /** Габариты в дюймах — из них считается объёмный вес и логистика в COGS. */
  dims?: { l: number | null; w: number | null; h: number | null };
}

/** Безопасное приведение к числу — NaN/undefined/null → 0. */
function n(v: unknown): number {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}

export function buildBackendPayload(
  calc?: CalculatorPayloadSource,
  salesPlan?: unknown,
  budget?: unknown,
  notes?: string,
): Record<string, unknown> {
  if (!calc) return {};
  return {
    parent_asin: calc.asin || '',
    purchase_price: n(calc.purchasePrice),
    shipping_price: n(calc.shippingPrice),
    shipping_cost_per_kg: n(calc.shippingCostPerKg),
    unit_weight_kg: n(calc.unitWeightKg),
    // Дюймы, как в самом калькуляторе. Без них загруженный расчёт терял
    // объёмный вес, а с ним и стоимость логистики в себестоимости.
    length_in: calc.dims?.l ?? null,
    width_in: calc.dims?.w ?? null,
    height_in: calc.dims?.h ?? null,
    storage_fee: n(calc.storageFee),
    selling_price: n(calc.sellingPrice),
    referral_fee: n(calc.referralFee),
    fba_fee: n(calc.fbaFee),
    other_fees: n(calc.otherFees),
    ppc_cost: n(calc.ppcCost),
    cost_per_click: n(calc.ppcCost),
    conversion_rate: n(calc.conversionRate),
    ppc_sales_percentage: n(calc.ppcSalesPercentage),
    return_rate: n(calc.returnRate),
    resellable_returns: n(calc.resellableReturns),
    monthly_sales: n(calc.monthlySales),
    batch_quantity: n(calc.batchQuantity),
    storage_fee_per_unit: n(calc.storageFee),
    coupon_promo: 0,
    coupon_promo_enabled: calc.couponPromoEnabled ?? true,
    competitors: (calc.competitorAsins || []).map((a) => ({ asin: a, price: 0 })),
    sales_plan_data: salesPlan || null,
    budget_data: budget || null,
    notes: notes || '',
  };
}
